import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, titleCase } from "@/lib/format";
import { PageHeader, DataTable, Pager, useList } from "@/components/shared";
import { FormDialog } from "@/components/EntityForm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BadgeCheck, Plus } from "lucide-react";

export const AGENT_FIELDS = [
  { name: "name", label: "Agent / Business Name", required: true },
  { name: "agent_type", label: "Agent Type", type: "select", options: ["individual", "proprietorship", "partnership", "company", "other"].map((v) => ({ value: v, label: titleCase(v) })) },
  { name: "mobile", label: "Mobile" },
  { name: "email", label: "Email" },
  { name: "dob", label: "Date of Birth", type: "date" },
  { name: "lines", label: "Business Lines", type: "select", options: [{ value: "insurance", label: "Insurance" }, { value: "loan", label: "Loan" }, { value: "both", label: "Insurance + Loan" }] },
  { name: "pan", label: "PAN (e.g. ABCDE1234F)" },
  { name: "aadhaar", label: "Aadhaar (only last 4 digits are stored)" },
  { name: "city", label: "City" },
  { name: "state", label: "State" },
  { name: "pincode", label: "PIN Code" },
  { name: "payout_percent_default", label: "Default Payout % of commission", type: "number" },
  { name: "address", label: "Address", type: "textarea", full: true },
];

export const prepAgent = (v) => {
  const out = { ...v };
  if (v.lines) out.business_lines = v.lines === "both" ? ["insurance", "loan"] : [v.lines];
  delete out.lines;
  if (!out.aadhaar) delete out.aadhaar;
  return out;
};

export default function Agents() {
  const { hasPerm } = useAuth();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [kyc, setKyc] = useState("all");
  const params = { ...(q ? { q } : {}), ...(kyc !== "all" ? { kyc_status: kyc } : {}) };
  const { items, total, loading, page, setPage, refetch } = useList("/agents", params);
  const create = async (v) => { await api.post("/agents", prepAgent(v)); refetch(); };

  const columns = [
    { key: "agent_code", header: "Agent ID", render: (r) => <span className="font-mono text-xs">{r.agent_code}</span> },
    { key: "name", header: "Agent", render: (r) => <div><div className="font-semibold">{r.name}</div><div className="text-xs text-muted-foreground">{titleCase(r.agent_type)} · {r.mobile || "—"}</div></div> },
    { key: "business_lines", header: "Lines", render: (r) => <span className="text-sm">{(r.business_lines || []).map(titleCase).join(", ")}</span> },
    { key: "city", header: "City", render: (r) => <span className="text-sm">{r.city || "—"}</span> },
    { key: "kyc_status", header: "KYC", render: (r) => <StatusBadge value={r.kyc_status} /> },
    { key: "gst_status", header: "GST", render: (r) => <StatusBadge value={r.gst_status} /> },
    { key: "bank_verification_status", header: "Bank", render: (r) => <StatusBadge value={r.bank_verification_status} /> },
  ];

  return (
    <div>
      <PageHeader title="Agents (POSP / DSA)" subtitle="KYC, GST, bank verification and payouts in one profile per agent" icon={BadgeCheck}
        actions={hasPerm("agents:add") && (
          <FormDialog title="Add Agent" fields={AGENT_FIELDS} initial={{ agent_type: "individual", lines: "insurance", payout_percent_default: 70 }}
            onSubmit={create} submitLabel="Add Agent" testId="create-agent-dialog"
            trigger={<Button data-testid="create-agent-btn"><Plus className="h-4 w-4 mr-1.5" /> Add Agent</Button>} />
        )} />
      <div className="flex flex-wrap gap-3 mb-4">
        <Input placeholder="Search name, mobile, agent ID…" value={q} onChange={(e) => setQ(e.target.value)} className="w-72" data-testid="agent-search-input" />
        <Select value={kyc} onValueChange={setKyc}>
          <SelectTrigger className="w-48" data-testid="agent-kyc-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["all", "pending", "under_review", "verified", "rejected"].map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All KYC statuses" : titleCase(s)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <DataTable columns={columns} rows={items} loading={loading} rowTestId="agent-table-row" onRowClick={(r) => nav(`/agents/${r.id}`)} />
      <Pager page={page} limit={25} total={total} onPage={setPage} />
    </div>
  );
}
